import { motion } from "framer-motion";
import { Calendar, Users, Flame, Image, Crown, Palette, Star } from "lucide-react";
import type { WrappedStats } from "@/hooks/useWrappedStats";
import Confetti from "./Confetti";

interface SummarySlideProps {
  stats: WrappedStats;
  firstName: string;
}

const SummarySlide = ({ stats, firstName }: SummarySlideProps) => {
  const topBestie = stats.top_besties[0];
  const topHost = stats.top_host;
  
  const statItems = [
    {
      icon: Calendar,
      value: stats.total_events,
      label: "Events",
      color: "text-secondary",
    },
    {
      icon: Users,
      value: topBestie?.first_name || "—",
      label: "Top Bestie",
      color: "text-pink-400",
    },
    {
      icon: Star,
      value: topHost?.first_name || "—",
      label: "Top Host",
      color: "text-cyan-400",
    },
    {
      icon: Flame,
      value: stats.longest_streak,
      label: "Week Streak",
      color: "text-orange-500",
    },
    {
      icon: Image,
      value: stats.galleries_visited,
      label: "Strolls",
      color: "text-purple-400",
    },
    {
      icon: Crown,
      value: stats.secret_salons_visited,
      label: "Secret Salons",
      color: "text-amber-400",
    },
  ];

  return (
    <div className="flex flex-col items-center justify-center text-center px-6 w-full max-w-md relative">
      {/* Confetti from both corners */}
      <Confetti corner="left" />
      <Confetti corner="right" />

      {/* Animated Nimi Logo */}
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ type: "spring", stiffness: 200 }}
        className="mb-4 relative"
      >
        <motion.img
          src="/lovable-uploads/cee94e78-b677-411d-9ba5-f432a1142eac.png"
          alt="Nimi"
          className="h-12 brightness-200"
          animate={{
            scale: [1, 1.1, 1],
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
      </motion.div>

      <motion.h2
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="text-2xl font-bold text-background mb-2"
      >
        {firstName}'s 2025
      </motion.h2>

      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3 }}
        className="text-background/70 mb-6"
      >
        Year in Art
      </motion.p>

      {/* Summary card */}
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.4 }}
        className="w-full bg-gradient-to-br from-background/10 to-background/5 backdrop-blur-sm rounded-2xl p-6 border border-background/20"
      >
        <div className="grid grid-cols-2 gap-4">
          {statItems.map((item, idx) => (
            <motion.div
              key={item.label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 + idx * 0.1 }}
              className={`p-4 rounded-xl bg-background/5 ${
                idx === 0 || idx === statItems.length - 1 ? "col-span-2" : ""
              }`}
            >
              <div className="flex items-center justify-center gap-2 mb-1">
                <item.icon className={`w-5 h-5 ${item.color}`} />
                <span className={`text-2xl font-bold ${item.color}`}>
                  {item.value}
                </span>
              </div>
              <p className="text-sm text-background/60">{item.label}</p>
            </motion.div>
          ))}
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2 }}
        className="mt-6 text-sm text-background/60 text-center"
      >
        <span>Here's to more art adventures in 2026!</span>
        <Palette className="w-4 h-4 text-secondary inline-block ml-1.5 align-middle" />
      </motion.div>

      {/* Nimi branding */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4 }}
        className="mt-8 text-center"
      >
        <span className="text-sm text-background/40">Made with </span>
        <span className="text-secondary font-bold">Nimi</span>
      </motion.div>
    </div>
  );
};

export default SummarySlide;
